import { useDrawerState } from "@/stores/drawer";
import { DataCollectionAccessor, useDataCollection } from "@soft-db/ui";
import { storeToRefs } from "pinia";
import { useFeaturesStore } from "../../../stores/features";
import { ItemObject, MonitoringObject, useDrawerData } from "../use-drawer-data";

export function useDrawerCollection(objects: MaybeRefOrGetter<MonitoringObject[]>) {
	const { collection: allItems, isLoading } = useDrawerData(objects);

	const featureStore = useFeaturesStore();

	const hasSite = computed(() => toValue(objects).includes("site"));
	const hasStation = computed(() => toValue(objects).includes("station"));
	const hasReport = computed(() => toValue(objects).includes("report"));

	const enableObjectFilter = computed(() => toValue(objects).length > 1);

	const drawerStore = useDrawerState();
	const { filter, items: drawerItems, isLoading: drawerLoading } = storeToRefs(drawerStore);

	const accessors = computed<DataCollectionAccessor<ItemObject>[]>(() => {
		const accessors: DataCollectionAccessor<ItemObject>[] = [
			{ field: "name", enableGlobalFilter: true },
		];

		if (hasSite.value || hasStation.value || hasReport.value) {
			accessors.push({ field: "description", enableGlobalFilter: true });
		}

		if (hasSite.value || hasStation.value) {
			accessors.push({ field: "status", enableColumnFilter: true });
		}

		// Only show the connected filter if the user can see both station and site
		if (
			(hasSite.value || hasStation.value) &&
			featureStore.hasFeature("station") &&
			featureStore.hasFeature("site")
		) {
			accessors.push({ field: "connected", enableColumnFilter: true });
		}

		// TODO: Add hasStation.value to enableColumnFilter?
		if (featureStore.hasFeature("space")) {
			accessors.push({
				id: "spaces",
				fieldFn: (object) => object.spaces.map((space) => space.name),
				enableColumnFilter: hasSite.value || hasReport.value,
			});
		}

		if (enableObjectFilter.value) {
			accessors.push({ field: "object", enableColumnFilter: true });
		}

		return accessors;
	});

	const fieldFilters = drawerStore.getFilters(
		accessors.value.map((a) => a.id || a.field).filter((id) => id != null)
	);

	const { state, items } = useDataCollection(allItems, accessors, { storage: "url" });

	if (filter.value) {
		state.filter = filter.value;
	}

	if (fieldFilters.value.length > 0) {
		state.fieldFilters = fieldFilters.value;
	}

	syncRef(
		computed(() => items.value.map((i) => i.item)),
		drawerItems,
		{ direction: "ltr" }
	);
	syncRef(isLoading, drawerLoading, { direction: "ltr" });
	syncRef(toRef(state, "fieldFilters"), fieldFilters, { direction: "ltr" });
	syncRef(toRef(state, "filter"), filter, { direction: "ltr" });

	return {
		state,
		isLoading,
		items,
	};
}
